import Decimal from 'decimal.js';
import * as yup from 'yup';

import {
  DietaryPreference,
  ItemDisplayUnit,
  ItemReturnPolicy,
  MandatoryWarning,
  OptionBinding,
  UserRequirement,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { VatInputType } from 'modules/menu/components/VatCategories';
import getMaxCurrencyDecimals from 'modules/menu/components/getCurrencyMaxDecimals';

import { FeedOverrideFieldNames, FormValues } from './types';
import { isValidBarcodeDigit, isValidBarcodeLength } from '../../../validation';
import { validationSchema as inventoryMngmtSchema } from '../Item/InventoryMngmtFormSection';
import { commissionsFormSchema } from '../Item/ItemCommissions/ItemCommissionsFormSection';

export type ValidationContext = {
  currency: Currency;
};

export const yupTestBarCodeLength = {
  name: 'barcodeLength',
  message: { id: 'validation.enterValidBarcode' },
  test: (value: string | undefined) => !value || isValidBarcodeLength(value),
};

export const yupTestBarcodeDigits = {
  name: 'barcodeCheckDigit',
  message: { id: 'validation.enterValidBarcodeDigit' },
  test: (value: string | undefined) => !value || isValidBarcodeDigit(value),
};

export const yupTestUpToDecimalPlaces = (decimals: number) => ({
  name: 'upToDecimalPlaces',
  message:
    decimals === 1 ? { id: 'validation.atMostOneDecimal' } : { id: 'validation.atMostNDecimals', values: { decimals } },
  test: (value: number | undefined) => value === undefined || new Decimal(value).decimalPlaces() <= decimals,
});

export const yupTestMonetaryDecimals = {
  name: 'monetaryDecimals',
  test: (value: number | undefined, context: yup.TestContext<ValidationContext>) => {
    const decimals = context.options.context?.currency ? getMaxCurrencyDecimals(context.options.context.currency) : 2;

    if (value !== undefined && new Decimal(value).decimalPlaces() > decimals) {
      return context.createError({
        message:
          decimals === 0
            ? { id: 'validation.mustBeInteger' }
            : { id: 'validation.atMostNDecimals', values: { decimals } },
      });
    }
    return true;
  },
};

const itemDetailsSchema = yup
  .object({
    alcoholPercentage: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(1)).required(),
    availabilityPeriod: yup
      .mixed<FormValues['availabilityPeriod']>()
      .test('endIsAfterStart', { id: 'validation.mustBeEndAfterStart' }, value => {
        if (value && value.start && value.end && value.end <= value.start) {
          return false;
        }
        return true;
      })
      .required(),
    barcodeGtin: yup.string().test(yupTestBarCodeLength).test(yupTestBarcodeDigits),
    brandId: yup.string().nullable().defined(),
    categoryId: yup.string().nullable().defined(),
    caffeineContentValue: yup
      .number()
      .nullable()
      .positive()
      .max(100_000)
      .when('caffeineContentUnits', (caffeineContentUnits, schema: yup.BaseSchema) =>
        !!caffeineContentUnits ? schema.required().test(yupTestUpToDecimalPlaces(1)) : schema,
      ),
    caffeineContentUnits: yup.mixed<FormValues['caffeineContentUnits']>(),
    description: yup.string().trim(),
    disableBikeDelivery: yup.boolean().required(),
    disableCarDelivery: yup.boolean().required(),
    disableMotorcycleDelivery: yup.boolean().required(),
    fixedDiscountPrice: yup
      .number()
      .min(0)
      .test(yupTestMonetaryDecimals)
      .when('discountType', (discountType: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
        discountType === 'absolute' ? schema.required() : schema,
      )
      .when('price', (price: number | undefined, schema: yup.NumberSchema) =>
        price !== undefined ? schema.lessThan(price) : schema,
      ),
    discountPercentage: yup
      .number()
      .min(0)
      .max(100)
      .test(yupTestUpToDecimalPlaces(0))
      .when('discountType', (discountType: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
        discountType === 'relative' ? schema.required() : schema,
      ),
    discountType: yup.mixed<FormValues['discountType']>(),
    volumeInMl: yup.number().positive(),
    weightInGrams: yup.number().positive(),
    numberOfUnits: yup.number().positive(),
    displayUnit: yup.mixed<ItemDisplayUnit>().nullable().defined(),
    dietaryPreferences: yup.mixed<DietaryPreference[]>().defined(),
    ingredients: yup.string(),
    allergens: yup.string(),
    additives: yup.string(),
    nutritionFacts: yup.string(),
    nutritionValues: yup.array().of(
      yup.object({
        id: yup.string().required(),
        value: yup.string().trim(),
      }),
    ),
    nutritionValuesBaseline: yup.mixed<'per_100g' | 'per_100ml'>().when('nutritionValues', {
      is: (nutritionValues: { id: string; value?: string }[]) => nutritionValues?.some(n => n.value !== undefined),
      then: schema => schema.required(),
    }),
    countryOfOrigin: yup.string(),
    usageInstructions: yup.string(),
    producerInfo: yup.string(),
    distributorInfo: yup.string(),
    bundleOffer: yup.boolean().required(),
    depositOption: yup.string(),
    returnPolicy: yup.mixed<ItemReturnPolicy['type'] | null>(),
    mandatoryWarnings: yup.mixed<MandatoryWarning[]>().defined(),
    maxQuantityPerPurchase: yup
      .number()
      .positive()
      .integer()
      .when(['weightType', 'sellByWeightInputType', 'gramsPerStep', 'minQuantityPerPurchase'], ((
        weightType: 'pre-packaged' | 'weighted-items' | undefined,
        sellByWeightInputType: 'grams' | 'number_of_items' | undefined,
        gramsPerStep: number | undefined,
        minQuantityPerPurchase: number | undefined,
      ) => {
        if (minQuantityPerPurchase !== undefined) {
          return yup.number().positive().integer().min(minQuantityPerPurchase);
        }
        return weightType === 'weighted-items' && sellByWeightInputType === 'grams' && gramsPerStep !== undefined
          ? yup.number().positive().integer().min(gramsPerStep, {
              id: 'validation.mustBeGreaterOrEqualThanMinimumWeight',
            })
          : yup.number().positive().integer();
        // it seems that the method overload is mistyped so I've cast to any to avoid TS error
      }) as any),
    minQuantityPerPurchase: yup
      .number()
      .positive()
      .integer()
      .when(['weightType', 'sellByWeightInputType', 'gramsPerStep'], ((
        weightType: 'pre-packaged' | 'weighted-items' | undefined,
        sellByWeightInputType: 'grams' | 'number_of_items' | undefined,
        gramsPerStep: number | undefined,
      ) => {
        return weightType === 'weighted-items' && sellByWeightInputType === 'grams' && gramsPerStep !== undefined
          ? yup.number().positive().integer().min(gramsPerStep, {
              id: 'validation.mustBeGreaterOrEqualThanMinimumWeight',
            })
          : yup.number().positive().integer();
        // it seems that the method overload is mistyped so I've cast to any to avoid TS error
      }) as any),
    enabled: yup.boolean().required(),
    enableDelivery: yup.boolean().required(),
    enableEatin: yup.boolean().required(),
    enableTakeaway: yup.boolean().required(),
    image: yup.mixed<FormValues['image']>().required(),
    merchantSku: yup.string().trim(),
    moreInformation: yup.string(),
    name: yup.string().trim().required(),
    posId: yup.string(),
    price: yup.number().min(0).test(yupTestMonetaryDecimals).required(),
    productHierarchyLevelId: yup.string(),
    sourceCountry: yup.string(),
    sourceConfig: yup.mixed<Record<FeedOverrideFieldNames, 'USER_INPUT' | undefined> | undefined>(),
    useWeeklyAvailability: yup.boolean().required(),
    useWeeklyVisibility: yup.boolean().required(),
    userRequirement: yup.mixed<UserRequirement>(),
    vat: yup
      .object({
        type: yup.mixed<VatInputType>(),
        categoryCode: yup.string(),
        percentage: yup.number(),
      })
      .required()
      .test(
        'categoryCode',
        'Please select VAT category',
        vat => vat.categoryCode !== undefined || vat.percentage !== undefined,
      )
      .test(
        'percentage',
        'VAT must be between 0% and 99%',
        vat =>
          vat.type !== 'manual' ||
          (vat.percentage !== undefined &&
            vat.percentage >= 0 &&
            vat.percentage <= 99 &&
            yupTestUpToDecimalPlaces(2).test(vat.percentage)),
      ),
    weeklyAvailability: yup.mixed<FormValues['weeklyAvailability']>().required(),
    weeklyVisibility: yup.mixed<FormValues['weeklyVisibility']>().required(),
    location: yup.string().trim(),
    pickingAreaId: yup.string(),
    pickingAreaSequence: yup.number().integer().positive(),
    weightType: yup.mixed<'pre-packaged' | 'weighted-items'>(),
    sellByWeightInputType: yup.mixed<'grams' | 'number_of_items'>().when('weightType', {
      is: (weightType: 'pre-packaged' | 'weighted-items') => weightType === 'weighted-items',
      then: schema => schema.required(),
    }),
    gramsPerStep: yup
      .number()
      .min(1)
      .when('sellByWeightInputType', {
        is: (sellByWeightInputType: 'grams' | 'number_of_items') =>
          sellByWeightInputType === 'grams' || sellByWeightInputType === 'number_of_items',
        then: schema => schema.required(),
      }),
    optionBindings: yup.array().of(yup.mixed<OptionBinding>().required()).required(),
    externalInfoHyperlink: yup
      .object({
        hyperlink: yup.string().notRequired(),
        text: yup.string().notRequired(),
      })
      .nullable()
      .shape(
        {
          hyperlink: yup.string().when('text', {
            is: undefined,
            then: yup.string().notRequired(),
            otherwise: yup.string().url().required(),
          }),
          text: yup.string().when('hyperlink', {
            is: undefined,
            then: yup.string().notRequired(),
            otherwise: yup.string().required(),
          }),
        },
        [['hyperlink', 'text']],
      ),
    isRestrictedItem: yup.boolean().required(),
    isWoltPlusOnly: yup.boolean(),
    isOTCRestricted: yup.boolean(),
    isAgeRestricted: yup.boolean(),
    ageLimit: yup.number().positive().optional(),
    isDeliveryHandshakeRestricted: yup.boolean(),
  })
  .required();

export const validationSchema = itemDetailsSchema.concat(inventoryMngmtSchema).concat(commissionsFormSchema);

export type ValidFormValues = yup.Asserts<typeof validationSchema>;
